/**
 * Frontend types for Filter Metadata System
 * Mirrors backend filter interfaces for type safety
 */

/**
 * Filter data types
 */
export enum FilterDataType {
    STRING = 'string',
    NUMBER = 'number',
    DATE = 'date',
    BOOLEAN = 'boolean',
    ARRAY = 'array',
}

/**
 * Filter input types for UI rendering
 */
export enum FilterInputType {
    TEXT = 'text',
    SELECT = 'select',
    MULTI_SELECT = 'multi_select',
    NUMBER_RANGE = 'number_range',
    DATE_RANGE = 'date_range',
    CHECKBOX = 'checkbox',
    BOOLEAN = 'boolean',
    /** Document relevance ranking - custom UI with minScore slider and cancer type */
    DOC_RANKER = 'doc_ranker',
}

/**
 * Filter operators
 */
export enum FilterOperatorType {
    EQUALS = 'equals',
    NOT_EQUALS = 'not_equals',
    CONTAINS = 'contains',
    NOT_CONTAINS = 'not_contains',
    IN = 'in',
    NOT_IN = 'not_in',
    GREATER_THAN = 'greaterThan',
    LESS_THAN = 'lessThan',
    BETWEEN = 'between',
    EXISTS = 'exists',
    NOT_EXISTS = 'not_exists',
}

/**
 * Metadata for a single filter field
 */
export interface FilterMetadata {
    field: string;
    label: string;
    description?: string;
    dataType: FilterDataType;
    inputType: FilterInputType;
    operators: FilterOperatorType[];
    defaultOperator: FilterOperatorType;
    requiresValues: boolean;
    valuesEndpoint?: string;
    relatedTable?: string;
    isRelatedFilter: boolean;
    category: string;
    isAdvanced?: boolean;
    placeholder?: string;
    validation?: {
        min?: number;
        max?: number;
        required?: boolean;
    };
}

/**
 * Response from filter metadata endpoint
 */
export interface FilterMetadataResponse {
    resource: string;
    filters: FilterMetadata[];
    categories: string[];
}

/**
 * Filter value option
 */
export interface FilterValueOption {
    value: string | number;
    label: string;
    count?: number;
    /** OMOP concept_id for concept-based filters */
    concept_id?: number;
    /** ICD code for diagnosis/procedure filters */
    icd_code?: string;
}

/**
 * Response from filter values endpoint
 */
export interface FilterValuesResponse {
    field: string;
    values: FilterValueOption[];
    total: number;
}

/**
 * Active filter state
 */
export interface ActiveFilter {
    field: string;
    operator: FilterOperatorType;
    value: any;
    label?: string;
}

/**
 * Filter group (for advanced filtering UI)
 */
export interface FilterGroup {
    id: string;
    operator: 'AND' | 'OR';
    filters: ActiveFilter[];
}
